import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import AuctionCountdown from '@/components/AuctionCountdown';
import InstagramFeed from '@/components/InstagramFeed';

const API = process.env.REACT_APP_BACKEND_URL;

const QUOTES = [
  {
    text: "Art is not what you see, but what you make others see.",
    author: "Edgar Degas"
  },
  {
    text: "Every artist was first an amateur.",
    author: "Ralph Waldo Emerson"
  },
  {
    text: "The purpose of art is washing the dust of daily life off our souls.",
    author: "Pablo Picasso"
  }
];

export default function HomePage() {
  const [artworks, setArtworks] = useState([]);
  const [currentAuction, setCurrentAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentQuote, setCurrentQuote] = useState(0);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artworksRes, auctionRes] = await Promise.all([
          axios.get(`${API}/api/artworks`),
          axios.get(`${API}/api/auctions/current`)
        ]);
        setArtworks(artworksRes.data);
        setCurrentAuction(auctionRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-rotate quotes
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentQuote(prev => (prev + 1) % QUOTES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Carousel slide items (artworks for the carousel)
  const carouselItems = artworks.slice(0, 4);

  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % carouselItems.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + carouselItems.length) % carouselItems.length);

  return (
    <div className="page-transition pt-28">
      {/* ===== EDITORIAL GRID — Basquiat-style cards ===== */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          {/* Card 1 — Featured Auction (large span) */}
          {currentAuction && (
            <Link
              to="/auction"
              className="lg:col-span-2 group block"
              data-testid="card-auction"
            >
              <div className="relative overflow-hidden">
                <img
                  src={currentAuction.artwork?.image_url}
                  alt={currentAuction.artwork?.title}
                  className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                    Monthly Auction
                  </span>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mb-2 leading-tight">
                    <strong>{currentAuction.title}</strong>
                  </h2>
                  <p className="text-[#A3A3A3] text-sm line-clamp-2 max-w-lg">
                    {currentAuction.description}
                  </p>
                </div>
              </div>
            </Link>
          )}

          {/* Card 2 — Featured Artwork */}
          {artworks[0] && (
            <Link to="/gallery" className="group block" data-testid="card-artwork-0">
              <div className="relative overflow-hidden">
                <img
                  src={artworks[0].image_url}
                  alt={artworks[0].title}
                  className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                    Exhibition
                  </span>
                  <h3 className="font-serif text-xl md:text-2xl text-[#F5F5F0] leading-tight">
                    <strong>{artworks[0].title}</strong>
                  </h3>
                  <p className="text-[#A3A3A3] text-sm mt-1">{artworks[0].year} &middot; {artworks[0].medium}</p>
                </div>
              </div>
            </Link>
          )}

          {/* Card 3, 4, 5 — More artwork cards */}
          {artworks.slice(1, 4).map((artwork, i) => (
            <Link
              to="/gallery"
              key={artwork.id || i}
              className="group block"
              data-testid={`card-artwork-${i + 1}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={artwork.image_url}
                  alt={artwork.title}
                  className="w-full h-[350px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                    {artwork.medium || 'Artwork'}
                  </span>
                  <h3 className="font-serif text-lg md:text-xl text-[#F5F5F0] leading-tight">
                    <strong>{artwork.title}</strong>
                  </h3>
                  <p className="text-[#A3A3A3] text-sm mt-1 line-clamp-2">{artwork.description}</p>
                </div>
              </div>
            </Link>
          ))}

          {/* Card 6 — News / Contact */}
          <Link to="/contact" className="group block" data-testid="card-contact">
            <div className="relative overflow-hidden bg-[#121212] h-[350px] flex flex-col justify-end p-6 border border-white/5 transition-colors group-hover:border-[#C5A059]/30">
              <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                News
              </span>
              <h3 className="font-serif text-xl text-[#F5F5F0] leading-tight mb-2">
                <strong>Commission &amp; Inquiries</strong>
              </h3>
              <p className="text-[#A3A3A3] text-sm">
                Interested in acquiring original works or commissioning a piece? Get in touch with the artist.
              </p>
            </div>
          </Link>

          {/* Remaining artworks */}
          {artworks.slice(4).map((artwork, i) => (
            <Link
              to="/gallery"
              key={artwork.id || i}
              className="group block"
              data-testid={`card-artwork-${i + 4}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={artwork.image_url}
                  alt={artwork.title}
                  className="w-full h-[350px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                    {artwork.medium || 'Artwork'}
                  </span>
                  <h3 className="font-serif text-lg md:text-xl text-[#F5F5F0] leading-tight">
                    <strong>{artwork.title}</strong>
                  </h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== FEATURED CAROUSEL — like basquiat.com bottom slider ===== */}
      {carouselItems.length > 0 && (
        <section className="relative bg-[#121212] border-y border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6">
            <div className="flex items-center gap-6 overflow-hidden">
              {/* Carousel viewport */}
              <div className="flex gap-6 transition-transform duration-500" style={{ transform: `translateX(-${currentSlide * 320}px)` }}>
                {carouselItems.map((item, i) => (
                  <Link
                    to="/gallery"
                    key={item.id || i}
                    className="flex-shrink-0 w-[300px] flex items-center gap-4 group"
                    data-testid={`carousel-item-${i}`}
                  >
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-20 h-20 object-cover flex-shrink-0"
                    />
                    <span className="text-sm text-[#A3A3A3] group-hover:text-[#F5F5F0] transition-colors line-clamp-2">
                      {item.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Slide dots */}
            <div className="flex justify-center gap-2 mt-4">
              {carouselItems.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    currentSlide === i ? 'bg-[#C5A059]' : 'bg-white/20'
                  }`}
                  data-testid={`carousel-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== LIFE & LEGACY — like basquiat.com timeline ===== */}
      <section className="py-20 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-4xl italic text-[#F5F5F0] mb-4">
            Life &amp; Legacy
          </h2>
          <p className="text-[#A3A3A3] max-w-2xl mx-auto">
            <strong>Explore the creative journey of Akanni</strong> — from bold brushstrokes to digital expression.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/about" className="group" data-testid="legacy-early">
            <div className="relative overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_8010d79a-e92b-49b5-9c1b-b21bc7b6963a/artifacts/p3ev29um_Screenshot_20180415-005858~2.png"
                alt="Early Works"
                className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-serif text-4xl text-[#F5F5F0]">Early Works</span>
              </div>
            </div>
          </Link>

          <Link to="/about" className="group" data-testid="legacy-canvas">
            <div className="relative overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_8010d79a-e92b-49b5-9c1b-b21bc7b6963a/artifacts/lbl08bcv_Screenshot_2019-08-19-17-10-42.png"
                alt="Canvas Era"
                className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-serif text-4xl text-[#F5F5F0]">Canvas Era</span>
              </div>
            </div>
          </Link>

          <Link to="/about" className="group" data-testid="legacy-digital">
            <div className="relative overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_akanni-imagery/artifacts/ecbjahvk_9df5efb4-fb30-49aa-a762-6e567aa35799-1_all_359.jpeg"
                alt="Digital Era"
                className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-serif text-4xl text-[#F5F5F0]">Digital Era</span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ===== QUOTES CAROUSEL — like basquiat.com quotes section ===== */}
      <section className="py-20 bg-[#121212] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center relative min-h-[200px]">
          {QUOTES.map((quote, i) => (
            <div
              key={i}
              className={`absolute inset-0 flex flex-col items-center justify-center px-6 transition-opacity duration-1000 ${
                currentQuote === i ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <blockquote className="font-serif text-2xl md:text-3xl italic text-[#F5F5F0] leading-relaxed mb-6">
                "{quote.text}"
              </blockquote>
              <footer className="font-mono text-xs uppercase tracking-[0.2em] text-[#C5A059]">
                &mdash; {quote.author}
              </footer>
            </div>
          ))}

          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-2">
            {QUOTES.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentQuote(i)}
                className={`w-8 h-[2px] transition-colors ${
                  currentQuote === i ? 'bg-[#C5A059]' : 'bg-white/20'
                }`}
                data-testid={`quote-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== AUCTION PREVIEW — Current bid highlight ===== */}
      {currentAuction && (
        <section className="py-20 max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="overflow-hidden">
              <img
                src={currentAuction.artwork?.image_url}
                alt={currentAuction.artwork?.title}
                className="w-full h-auto max-h-[500px] object-cover"
              />
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                Current Auction
              </span>
              <h2 className="font-serif text-4xl text-[#F5F5F0] tracking-tight mb-6 leading-tight">
                {currentAuction.artwork?.title}
              </h2>
              <div className="mb-6">
                <AuctionCountdown endDate={currentAuction.end_date} />
              </div>
              <div className="p-6 border border-white/10 mb-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] mb-2">Current Highest Bid</p>
                <p className="font-serif text-4xl text-[#C5A059]">${currentAuction.highest_bid?.toLocaleString()}</p>
              </div>
              <Link
                to="/auction"
                className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0A0A0A] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#E0BE77] transition-colors"
                data-testid="home-place-bid"
              >
                Place Your Bid <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ===== INSTAGRAM FEED ===== */}
      <InstagramFeed />

      {/* ===== NEWSLETTER SIGNUP — like basquiat.com footer CTA ===== */}
      <section className="py-20 bg-[#121212] border-t border-white/5">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mb-4">
            <strong>Stay Connected</strong>
          </h3>
          <p className="text-[#A3A3A3] text-sm mb-8">
            Sign up to be the first to know about new artworks, exhibitions, and auction updates from Phaeton Imagery.
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); setEmail(''); }}
            className="flex gap-0"
            data-testid="newsletter-form"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email Address"
              required
              className="flex-1 bg-transparent border border-white/20 border-r-0 px-4 py-3 text-[#F5F5F0] text-sm placeholder:text-[#525252] focus:outline-none focus:border-[#C5A059]"
              data-testid="newsletter-email"
            />
            <button
              type="submit"
              className="bg-[#C5A059] text-[#0A0A0A] px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#E0BE77] transition-colors"
              data-testid="newsletter-submit"
            >
              Subscribe
            </button>
          </form>
          <p className="text-[#525252] text-xs mt-4">
            Follow us on{' '}
            <a href="https://instagram.com/phaetonimagery" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:text-[#E0BE77]">Instagram</a>
          </p>
        </div>
      </section>
    </div>
  );
}
